import React from "react";
import type { AspectId } from "./frame";
import { getFrame, computeLayout } from "./frame";
import type { Theme } from "./theme";
import { sceneMotion } from "./motion";
import { Background } from "./prims";
import { SafeGuides } from "./prims";
import { AmbientMotion } from "./visuals";
import type { SceneDef } from "./scenes";
import { cameraAt, cameraTransform } from "./camera";
import { BrandOverlay, type BrandKit } from "./brand";

/**
 * Anivaze v4 — the render Stage. `SceneDef + Theme + Frame(aspect) + t → SVG`.
 * The same component drives the live preview and (later) the frame-accurate
 * exporter. Nothing here is aspect-specific: the frame's viewBox + the scene's
 * layout-driven placement do the reflow. See PLAN §3–4.
 */
export const Stage: React.FC<{
  aspect: AspectId;
  theme: Theme;
  scene: SceneDef;
  t: number;
  showGuides?: boolean;
  /** Per-beat group entrance/exit. Off in film mode, where cross-beat blending
   *  is owned by the FilmPlayer's transition layers. Default on. */
  motion?: boolean;
  /** Optional brand kit — header/footer/watermark/logo drawn on top, fixed. */
  brand?: BrandKit | null;
}> = ({ aspect, theme, scene, t, showGuides, motion = true, brand }) => {
  const frame = getFrame(aspect);
  const layout = computeLayout(frame);
  const m = motion ? sceneMotion(t, scene.dur, layout.unit, theme.motion) : null;
  const cx = frame.w / 2;
  const cy = frame.h / 2;
  // Hand-drawn "sketch" pass: a subtle turbulence-displacement gives every actor
  // a wobbly, ink-on-paper feel (the Whiteboard / Writer YouTube style). Opt-in
  // per theme via `roughness`. The Chalkboard has its own board renderer, so it
  // is excluded to avoid a double effect. Deterministic (fixed seed).
  const sketch = theme.roughness > 0 && theme.id !== "chalk";
  const fid = `hd-${theme.id}`;
  // CAMERA (camera.ts): if the scene lays content out in a world bigger than the
  // frame, pan/zoom/scroll across it as f(t). Composed OUTSIDE the entrance motion
  // (world→screen), while Background/Ambient stay outside the camera so they don't
  // scroll. Absent camera → identity, no transform, no regression.
  const camT = scene.camera && scene.camera.length ? cameraTransform(cameraAt(scene.camera, t), frame) : "";
  const motionT = m ? `translate(0 ${m.translateY}) translate(${cx} ${cy}) scale(${m.scale}) translate(${-cx} ${-cy})` : "";
  const groupT = [camT, motionT].filter(Boolean).join(" ") || undefined;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${frame.w} ${frame.h}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${scene.label} — ${theme.label} theme, ${aspect}`}
      style={{ display: "block", width: "100%", height: "100%" }}
    >
      {sketch && (
        <defs>
          <filter id={fid} x="-4%" y="-4%" width="108%" height="108%">
            <feTurbulence type="fractalNoise" baseFrequency="0.02 0.022" numOctaves={2} seed={4} result="n" />
            <feDisplacementMap in="SourceGraphic" in2="n" scale={2 + theme.roughness * 4.8} xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      )}
      <Background theme={theme} frame={frame} />
      <AmbientMotion frame={frame} theme={theme} t={t} />
      <g
        opacity={m ? m.opacity : 1}
        transform={groupT}
        filter={sketch ? `url(#${fid})` : undefined}
      >
        {scene.render({ t, dur: scene.dur, layout, theme })}
      </g>
      <BrandOverlay brand={brand} frame={frame} theme={theme} unit={layout.unit} />
      {showGuides && <SafeGuides layout={layout} theme={theme} />}
    </svg>
  );
};
